import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { clamp } from '@src/services/timeline';

// Only re-seek when the audio drifts more than this — nudging every frame would stutter.
const DRIFT_THRESHOLD_SECONDS = 0.3;

/**
 * Plays the background-music track during preview. It follows the shared clock
 * (`getCurrentSec` / `isPlaying`) without driving the video, syncing one <audio>
 * to whichever clip the playhead is over. Volume = clip level (0..100) × track
 * level (0..1), so the track's mute (level 0) mutes it too.
 */
export const useMusicPlayback = ({
  enabled = false,
  isPlaying = false,
  getCurrentSec,
} = {}) => {
  const musicClips = useSelector((state) => state.project.musicClips);
  const musicVolume = useSelector((state) => state.project.volumes?.music);

  const audioRef = useRef(null);
  if (audioRef.current === null) audioRef.current = new Audio();
  // The src already loaded (skip reloading it) and the clip now playing (so
  // re-entering a clip re-seeks to its offset instead of resuming).
  const loadedLinkRef = useRef(null);
  const activeClipRef = useRef(null);

  const clips = useMemo(
    () => [...(musicClips || [])].sort((a, b) => a.startSec - b.startSec),
    [musicClips],
  );
  // Let the rAF loop read the latest clips / volume without restarting.
  const clipsRef = useRef(clips);
  useEffect(() => {
    clipsRef.current = clips;
  }, [clips]);
  const musicVolumeRef = useRef(musicVolume);
  useEffect(() => {
    musicVolumeRef.current = musicVolume;
  }, [musicVolume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!enabled || !isPlaying || !getCurrentSec) {
      audio.pause();
      activeClipRef.current = null;
      return undefined;
    }

    let rafId = 0;
    const tick = () => {
      const t = getCurrentSec();
      // Clips don't overlap, so the first one whose slot covers `t` is the one.
      const clip = clipsRef.current.find(
        (c) => t >= c.startSec && t < c.endSec,
      );
      if (clip) {
        const offset = (clip.trimStart || 0) + (t - clip.startSec);
        const volume = clamp(
          (clip.volume / 100) * musicVolumeRef.current,
          0,
          1,
        );
        if (activeClipRef.current?.id !== clip.id) {
          // Entered a clip: load its audio (once) and start at the right offset.
          activeClipRef.current = clip;
          if (loadedLinkRef.current !== clip.link) {
            loadedLinkRef.current = clip.link;
            audio.src = clip.link;
          }
          audio.currentTime = offset;
          audio.volume = volume;
          audio.play().catch(() => {});
        } else {
          // Same clip: keep the volume live and nudge back only on real drift.
          audio.volume = volume;
          if (Math.abs(audio.currentTime - offset) > DRIFT_THRESHOLD_SECONDS) {
            audio.currentTime = offset;
          }
          if (audio.paused) audio.play().catch(() => {});
        }
      } else if (activeClipRef.current) {
        // In a gap between clips: silence.
        activeClipRef.current = null;
        audio.pause();
      }
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(rafId);
      audio.pause();
      activeClipRef.current = null;
    };
  }, [enabled, isPlaying, getCurrentSec]);

  // Release the element on unmount.
  useEffect(
    () => () => {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
    },
    [],
  );
};

export default useMusicPlayback;
